import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormGroupType } from '@app/core/utils/formgroup-type';
import { CreateMeteringPointCommand, UpdateMeteringPointCommand, AddressResultType, AddressValidationOutcome } from '@app/api/dmc';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SearchComponent } from '@shared/components/input/search/search.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AutocompleteService } from '@app/core/services/autocomplete.service';
import { ToastService } from '@app/core/services/toast.service';
import { AlertService } from '@app/core/services/alert.service';
import { NgClass } from '@angular/common';
import { SearchConfig } from '@shared/components/input/search/search-config.interface';
import { Observable, of } from 'rxjs';
import { SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { map } from 'rxjs/operators';
import { DarSearchResult, mapDarResults } from '@app/core/utils/map-dar-results';
import { patchAddress } from '@app/core/utils/patch-address';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { AddressDto, ConnectionPointDto } from '@app/api/nts';
import { AddressType } from '@app/core/types/address-type';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { SelectDirective } from '@shared/components/input/select/select.directive';

@Component({
  selector: 'app-meteringpoint-form-address',
  imports: [
    ReactiveFormsModule,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    SearchComponent,
    TranslatePipe,
    NgClass,
    GuideCardComponent,
    SelectDirective,
    FormsModule,

  ],
  templateUrl: './meteringpoint-form-address.component.html',
  styleUrl: './meteringpoint-form-address.component.css'
})
export class MeteringpointFormAddressComponent implements OnInit {
  @ViewChild('search', { static: true }) search!: SearchComponent;

  @Input() public form!: FormGroupType<CreateMeteringPointCommand | UpdateMeteringPointCommand | ConnectionPointDto>;

  @Input() public formGroupName: string = 'installationLocationAddress';

  @Input() public fullWidth: boolean = true;

  @Input() overrideTitle?: string;

  public advancedInput: boolean = false;
  public addressFetched: boolean = false;
  public addressType: AddressType = 'address';

  public addressCarry?: AddressDto;

  /** Search Config **/
  public searchConfig: SearchConfig = {
    hotkeys: false,
    standalone: true,
    autoFocus: false,
    navigateUrl: undefined,
    callBackFunction: (value: string) => this.searchQuery(value),
  }

  public addressTypeList: KeyValue[] = [
    { key: 'address', value: 'enum.address.address' },
    { key: 'access', value: 'enum.address.access' },
  ]

  constructor(
    private readonly autocompleteService: AutocompleteService,
    private readonly toastService: ToastService,
    private readonly alertService: AlertService,
  ) {
  }

  public ngOnInit(): void {
    this.form.get(this.formGroupName)?.disable();
  }

  /** Search Query **/
  public searchQuery(value: string): Observable<SearchResultCollection[]> {
    if (value.length > 2) {
      const type = this.addressType;
      return this.autocompleteService.searchAddress(value, type)
        .pipe(
          map(results => mapDarResults(
            results,
            type,
            text => this.autocompleteService.searchAddress(text, type),
            id => this.autocompleteService.getByAccessAddressId(id),
          )),
        )
    }
    return of([]);
  }

  public addressSelected(event: DarSearchResult): void {
    if (event.id) {
      const type = event.type === AddressResultType.AccessAddress ? 'access' : 'address';
      this.autocompleteService.getById(event.id, type).subscribe((addr) => {
        /** Patch Address **/
        patchAddress(this.form, addr, this.formGroupName);

        /** Address Fetched **/
        this.addressFetched = true;
      })
    }
  }

  public sanitizeAddress(): void {
    this.form.markAsTouched();

    const group = this.form.get(this.formGroupName);
    if (!group?.valid) return;

    const addr = [
      group.get('streetName')?.value,
      group.get('buildingNumber')?.value,
      group.get('floorIdentification')?.value,
      group.get('roomIdentification')?.value,
      group.get('postalCode')?.value,
      group.get('cityName')?.value,
    ].filter(Boolean).join(' ').replace(/ {2,}/g, ' ').trim();

    this.autocompleteService.validateAddress(addr).subscribe((result) => {
      if (result.outcome === AddressValidationOutcome.Validated && result.address) {
        patchAddress(this.form, result.address, this.formGroupName);
        this.toastService.show('address.addresssanitized');
        return;
      }

      this.alertService.open('header', 'danger', MeteringpointFormAddressComponent.validationMessage(result.outcome));
    });
  }

  private static validationMessage(outcome: AddressValidationOutcome): string {
    switch (outcome) {
      case AddressValidationOutcome.MultipleMatches:
        return 'address.multiplematches';
      case AddressValidationOutcome.NotFound:
        return 'address.notfound';
      default:
        return 'address.notunique';
    }
  }

  public activateSearch(): void {
    this.advancedInput = false;

    if (this.addressCarry) {
      const addr = this.form.get(this.formGroupName)?.value;
      const { darReference: _, ...existingAddrRest } = this.addressCarry;
      const { darReference: __, ...addrRest } = addr;

      if (JSON.stringify(existingAddrRest) === JSON.stringify(addrRest)) {
        this.form.get(this.formGroupName)?.patchValue(this.addressCarry);
      }
    }

    this.form.get(this.formGroupName)?.disable();
    this.form.get(`${this.formGroupName}.streetName`)?.removeValidators(Validators.required);
    this.form.get(`${this.formGroupName}.buildingNumber`)?.removeValidators(Validators.required);
    this.form.get(`${this.formGroupName}.postalCode`)?.removeValidators(Validators.required);
    this.form.get(`${this.formGroupName}.country`)?.removeValidators(Validators.required);
  }

  public activateAdvanced(): void {
    this.advancedInput = true;

    this.addressCarry = this.form.get(this.formGroupName)?.value;

    this.form.get(this.formGroupName)?.enable();
    this.form.get(`${this.formGroupName}.streetName`)?.setValidators(Validators.required);
    this.form.get(`${this.formGroupName}.buildingNumber`)?.setValidators(Validators.required);
    this.form.get(`${this.formGroupName}.postalCode`)?.setValidators(Validators.required);
    this.form.get(`${this.formGroupName}.country`)?.setValidators(Validators.required);

    // Reset darReference
    this.form.get(`${this.formGroupName}.darReference`)?.reset();
    this.form.get(`${this.formGroupName}.darReference`)?.disable();
  }
}
